import random from "./global-prng";
import {
  createRandomCleanEncoding,
  SkipMode,
  FlipMode,
  FillMode,
  StandardRules,
  encode,
  binaryStringToByteLE,
  byteToBinaryStringLE,
} from "bitframes";

const randomRange = (min, max) => random.random() * (max - min) + min;
const randomRangeFloor = (min, max) => Math.floor(randomRange(min, max));
const randomNibble = () => random.rangeFloor(0, 16);
const randomByte = () => random.rangeFloor(0, 256);
const random2Bit = () => random.pick([0, 1, 2, 3]);

const createPureRandomLayer = () => {
  return {
    visible: random.boolean(),
    colors: [randomNibble(), randomNibble()],
    dimensions: [randomNibble(), randomNibble()], // 8 bits
    pattern: randomByte(), // 8 bits
    rule: randomByte(), // 8 bits
    scale: randomNibble(), // 4 bits
    skip: randomNibble(), // 4 bits
    skipMode: random2Bit(), // 2 bits
    fillMode: random2Bit(), // 2 bits
    flipMode: random2Bit(), // 2 bits
    wrap: random.boolean(), // 1 bit
  };
};

const createLayerWithOverrides = (overrides = {}) => {
  const defaults = createPureRandomLayer();
  const ret = { ...defaults };
  if (overrides) {
    for (let k in overrides) {
      if (overrides[k] != null) {
        ret[k] = overrides[k];
      }
    }
  }
  return ret;
};
const arraySize16WithoutZero = Array(16)
  .fill()
  .map((_, i) => i)
  .filter((i) => i !== 0);

function randomlyReversed(list) {
  if (random.boolean()) {
    const a = list.slice();
    a.reverse();
    return a;
  } else {
    return list.slice();
  }
}

export const randomizeDocSeed = (doc) => {
  return {
    ...doc,
    seed: random.rangeFloor(0, 8),
  };
};

export const nextRule =
  (dir = 1) =>
  (layers) =>
    mapVisibleLayers(layers, (layer) => {
      return {
        ...layer,
        rule: mod(layer.rule + dir, 256),
      };
    });

export const shuffleColors = (layers) =>
  mapVisibleLayers(layers, (layer) => {
    return {
      ...layer,
      colors: layer.colors.map((c) => {
        if (c == 0) return 0;
        const others = arraySize16WithoutZero.slice().filter((i) => i !== c);
        return random.pick(others);
      }),
    };
  });

export const toGrid = (layers) =>
  mapVisibleLayers(layers, (layer) => {
    const n = Math.round((layer.dimensions[0] + layer.dimensions[1]) / 2);
    return {
      ...layer,
      dimensions: [n, n],
    };
  });

export function fill(fn, n = 5) {
  return Array(n).fill().map(fn);
}

export const shuffleLayers = (layers) => {
  return random.shuffle(layers);
};

export const shufflePattern = (layers) =>
  mapVisibleLayers(layers, (layer) => {
    return {
      ...layer,
      pattern: byteToBinaryStringLE(randomByte()),
    };
  });

export const shufflePatternRuleSet = (layers) =>
  mapVisibleLayers(layers, (layer) => {
    return {
      ...layer,
      rule: randomByte(),
      fillMode: random2Bit(),
      wrap: random.boolean(),
      flipMode: random2Bit(),
      pattern: byteToBinaryStringLE(randomByte()),
    };
  });

export const shuffleSkip = (layers) =>
  mapVisibleLayers(layers, (layer) => {
    return {
      ...layer,
      // scale: randomNibble(), // 4 bits
      skip: random.rangeFloor(0, 16), // 4 bits
      skipMode: random2Bit(), // 2 bits
    };
  });

export const shuffleFlip = (layers) =>
  mapVisibleLayers(layers, (layer) => {
    return {
      ...layer,
      flipMode: random2Bit(), // 2 bits
    };
  });

export const noScale = (layers) =>
  mapVisibleLayers(layers, (layer) => {
    return {
      ...layer,
      scale: 1,
    };
  });

export const noSkip = (layers) =>
  mapVisibleLayers(layers, (layer) => {
    return {
      ...layer,
      // scale: randomNibble(), // 4 bits
      skip: 0, // 4 bits
      skipMode: SkipMode.EQUAL, // 2 bits
    };
  });

export const shuffleScale = (layers) =>
  mapVisibleLayers(layers, (layer) => {
    return {
      ...layer,
      scale: randomNibble(), // 4 bits
    };
  });

export const shuffleRule = (layers) =>
  mapVisibleLayers(layers, (layer) => {
    return {
      ...layer,
      rule: randomByte(), // 4 bits
    };
  });

export const createGlitch = () => {
  const nLayers = 3; //random.rangeFloor(3, 6);
  // const pal = random.shuffle(random.pick([[2, 1, 3]]));
  const pal = [2, ...randomlyReversed([1, 3])];
  const square = false; //random.boolean();
  const n = random.rangeFloor(6, 16);
  const n2 = square ? n : random.rangeFloor(6, 16);
  return fill((_, i) => {
    const a = i === nLayers - 1 ? 2 : pal[i % pal.length];
    const b = 0;
    const data = createLayerWithOverrides({
      dimensions: [n, n2],
      visible: i < nLayers,
      colors: randomlyReversed([pal[i] || 1, 0]),
      skip: i === nLayers - 1 ? 16 : 0,
      skipMode: random.pick([SkipMode.SCALED_COLUMNS]),
      scale: i === nLayers - 1 ? random.rangeFloor(1, 16) : 1,
    });
    return data;
  });
};

const withCleanShaping = (fn) => {
  return () => {
    return createRandomCleanEncoding(random, {
      createRandomEncoding: () => {
        const layers = fn();
        return encode({ frame: 0x00, system: 0x00, layers });
      },
    });
  };
};

const withRawBytes = (fn) => {
  return () => {
    const layers = fn();
    return encode({ frame: 0x00, system: 0x00, layers });
  };
};

export const createFractalRaw = () => {
  const nLayers = 2;
  // const possibleColors =
  // const pal = randomlyReversed([color, 0]);
  const square = true; //random.boolean();
  const n = random.rangeFloor(9, 16);
  const n2 = square ? n : random.rangeFloor(6, 16);
  const colors = Array(16)
    .fill()
    .map((_, i) => i)
    .filter((i) => i >= 3);
  const duo = random.shuffle(colors).slice(0, 2);
  // const color = random.rangeFloor(3, 16);
  // const offset = 1;
  // let c2 = color + offset;
  // if (c2 >= 16) c2 = 2 + offset;
  return fill((_, i) => {
    const data = createLayerWithOverrides({
      dimensions: i == 0 ? [0, 0] : [n, n2],
      visible: i < nLayers,
      colors: i === 0 ? [duo[0], 0] : randomlyReversed([duo[1], 0]),
      // pattern: i == 0 ? 0xff : undefined,
      pttern:
        i == 0
          ? 0xff
          : binaryStringToByteLE(random.shuffle("10101010".split("")).join("")),
      rule:
        i == 0
          ? StandardRules.IDENTITY
          : random.pick([
              18, 22, 26, 30, 45, 60, 82, 86, 90, 102, 105, 110, 124, 126, 129,
              135, 137, 146, 150, 153, 154, 161, 165, 167, 193, 195, 210, 218,
            ]),
      skip: 0,
      skipMode: SkipMode.SCALED_COLUMNS,
      fillMode: FillMode.REPEAT,
      scale: i == 0 ? 1 : 1,
    });
    return data;
  });
};

export const createMorse = withCleanShaping(() => {
  const nLayers = 1;
  // const possibleColors =
  // const pal = randomlyReversed([color, 0]);
  const square = random.boolean();
  const n = random.rangeFloor(7, 14);
  const n2 = square ? n : random.rangeFloor(7, 14);
  const colors = Array(16)
    .fill()
    .map((_, i) => i)
    .filter((i) => i > 3);
  const duo = random.shuffle(colors).slice(0, 2);
  // const color = random.rangeFloor(3, 16);
  // const offset = 1;
  // let c2 = color + offset;
  // if (c2 >= 16) c2 = 2 + offset;
  return fill((_, i) => {
    const data = createLayerWithOverrides({
      dimensions: i <= nLayers ? [n, n2] : undefined,
      visible: i <= nLayers,
      colors:
        i <= nLayers ? randomlyReversed([i == 0 ? 2 : duo[i], 0]) : undefined,
      pttern:
        i <= nLayers
          ? binaryStringToByteLE(random.shuffle("10101010".split("")).join(""))
          : undefined,
      rule:
        i <= nLayers
          ? random.pick([
              18, 22, 26, 30, 45, 60, 82, 86, 90, 102, 105, 110, 124, 126, 129,
              135, 137, 146, 150, 153, 154, 161, 165, 167, 193, 195, 210, 218,
            ])
          : undefined,
      skip: i <= nLayers ? random.rangeFloor(10, 16) : undefined,
      skipMode: SkipMode.SCALED_COLUMNS,
      fillMode: FillMode.REPEAT,
      scale: 1,
    });
    return data;
  });
});

export const createScanlines = withRawBytes(() => {
  const nLayers = 3;
  // const possibleColors =
  // const pal = randomlyReversed([color, 0]);
  const square = true; //random.boolean();
  const n = random.rangeFloor(14, 16); //random.rangeFloor(8, 16);
  const n2 = square ? n : random.rangeFloor(6, 16);
  const color = random.rangeFloor(4, 16);
  return fill((_, i) => {
    let pattern = 0xff;
    if (i == 1) pattern = binaryStringToByteLE("10101010");
    else if (i > 1) pattern = undefined;
    let rule = StandardRules.IDENTITY;
    //
    if (i == 1) rule = random.pick([62, 99, 109, 118, 131, 163]);
    else if (i > 1) rule = undefined;
    let dims = [0, 0];
    if (i == 1) dims = [n, n];
    else if (i > 1) dims = undefined;

    const data = createLayerWithOverrides({
      dimensions: dims,
      visible: i < nLayers,
      colors: i === 0 ? [2, 0] : [color, 0],
      wrap: false,
      pattern: pattern,
      rule,
      skip: i <= 1 ? 0 : random.rangeFloor(1, 16),
      skipMode: SkipMode.SCALED_COLUMNS,
      fillMode: FillMode.REPEAT,
      flipMode: undefined,
      scale: i == 0 ? 1 : 1,
    });
    return data;
  });
});

export const createDither = withCleanShaping(() => {
  const nLayers = 2; //random.pick([1, 2]);
  // const possibleColors =
  // const pal = randomlyReversed([color, 0]);
  const square = true; //random.boolean();
  const n = random.rangeFloor(12, 15); //random.rangeFloor(8, 14); //random.rangeFloor(8, 16);
  const n2 = random.rangeFloor(4, 16);
  // const color = random.rangeFloor(4, 16);
  const swap = false; //random.boolean();
  const curWhite = 1; // nLayers == 1 ? 1 : 2;
  //random.rangeFloor(8, 16)
  return fill((_, i) => {
    let opts = null;
    if (swap && i <= 1) i = i == 0 ? 1 : 0;
    if (i < nLayers) {
      opts = {
        dimensions: i == 0 ? [n, n] : [n2, n2],
        visible: true,
        colors: randomlyReversed([
          i == 0 ? random.pick([1, curWhite]) : random.rangeFloor(4, 16),
          0,
        ]),
        wrap: random.boolean(),
        // pattern: binaryStringToByteLE(
        //   random.shuffle("11111100".split("")).join("")
        // ),
        rule: randomByte(),
        skip: random.rangeFloor(8, 16),
        skipMode: SkipMode.SCALED_COLUMNS,
        fillMode: FillMode.REPEAT,
        scale: random.rangeFloor(0, 16),
        // flipMode: FillMode.NON,
        // scale: i == 0 ? 1 : 1,
      };
    } else {
      opts = { visible: false };
    }
    const data = createLayerWithOverrides(opts);
    return data;
  });
});

export const createDual = withRawBytes(() => {
  const nLayers = 1; //random.pick([1, 2]);
  // const possibleColors =
  // const pal = randomlyReversed([color, 0]);
  const square = true; //random.boolean();
  const n = random.rangeFloor(10, 15); //random.rangeFloor(8, 14); //random.rangeFloor(8, 16);
  const n2 = random.rangeFloor(1, 15);
  // const color = random.rangeFloor(4, 16);
  const swap = false; //random.boolean();
  const curWhite = 1; // nLayers == 1 ? 1 : 2;
  const possibleColors = Array(16)
    .fill()
    .map((_, i) => i)
    .filter((i) => i !== 0 && i !== 2 && i !== 3);
  // .filter((i) => i > 3);
  const colors = random.shuffle(possibleColors);

  //random.rangeFloor(8, 16)
  return fill((_, i) => {
    let opts = null;
    if (i < nLayers) {
      opts = {
        dimensions: randomlyReversed([n, n2]),
        skip: 0,
        skipMode: SkipMode.SCALED_COLUMNS,
        fillMode: FillMode.REPEAT,
        // wrap: false,
        visible: true,
        colors: randomlyReversed([colors[1], colors[0]]),
      };
    } else {
      opts = { visible: false };
    }
    const data = createLayerWithOverrides(opts);
    return data;
  });
});

export const createSoloRaw = () => {
  const nLayers = 1;
  const color = random.rangeFloor(4, 16);
  const pal = randomlyReversed([color, 0]);
  const square = random.boolean();
  const n = random.rangeFloor(6, 16);
  const n2 = square ? n : random.rangeFloor(6, 16);
  return fill((_, i) => {
    const data = createLayerWithOverrides({
      dimensions: i === 0 ? [n, n2] : undefined,
      visible: i < nLayers,
      colors: i === 0 ? pal : undefined,
      // skip: i === 0 ? random.rangeFloor(0, 16) : 0,
      skipMode: i == 0 ? random.pick([SkipMode.SCALED_COLUMNS]) : undefined,
      // scale: i == 0 ? 1 : undefined,
    });
    return data;
  });
};

export const createSolo = withCleanShaping(createSoloRaw);
export const createFractal = withCleanShaping(createFractalRaw);

export const createTextile = () => {
  const nLayers = 3;
  const pal = random
    .shuffle(arraySize16WithoutZero)
    .filter((f) => f !== 1 && f !== 2 && f !== 6)
    .slice(0, 3);
  const minSizes = [4, 4, 10];
  const maxSizes = [4, 10, 14];
  return fill((_, i) => {
    let [a, b] = random.shuffle(pal);
    if (i > 0) b = 0;
    const min = minSizes[i % minSizes.length];
    const max = maxSizes[i % maxSizes.length];
    const data = createLayerWithOverrides({
      dimensions: [random.rangeFloor(min, max), random.rangeFloor(min, max)],
      colors: randomlyReversed([a, b]),
      visible: i < nLayers,
      skip: i === 0 ? 0 : random.rangeFloor(0, 16),
    });
    return data;
  });
};

export const createCheckerBoard = () => {
  return fill((_, i) => {
    const nLayers = 1;
    const n = 8;
    const otherColors = Array(16)
      .fill()
      .map((_, i) => i)
      .filter((i) => i !== 0 && i !== 2);
    return createLayerWithOverrides({
      colors: randomlyReversed([2, random.pick(otherColors)]),
      visible: i < nLayers,
      dimensions: [n, n],
      skip: 0,
      // scale: 1,
      // pattern: "10101010",
      // flipMode: FlipMode.NONE,
      // horizontal: false,
      skipMode: SkipMode.EQUAL,
      fillMode: FillMode.REPEAT, // 2 bits
      // flipMode: random.pick(FlipMode.NONE),
      wrap: true, // 1 bit
      rule: StandardRules.COMPLEMENT,
    });
  });
};

export const randomGrid = (layers) =>
  mapVisibleLayers(layers, (layer) => {
    return {
      ...layer,
      dimensions: [random.rangeFloor(0, 16), random.rangeFloor(0, 16)],
    };
  });

export const randomSingleColor = (layers) =>
  mapVisibleLayers(layers, (layer) => {
    const color = random.rangeFloor(1, 16); // any non-zero
    const colors = [color, 0];
    return {
      ...layer,
      colors: randomlyReversed(colors),
    };
  });

export const randomDualColor = (layers) =>
  mapVisibleLayers(layers, (layer) => {
    const [a, b] = random.shuffle(arraySize16WithoutZero);
    const colors = [a, b];
    return {
      ...layer,
      colors,
    };
  });

// export const monochrome = (layers) => {
//   return mapVisibleLayers(layers, (layer) => {
//     const colors = layer.colors.map((color) => {
//       if (color === 0) return color;
//       const hex = colorOf(color);
//       const hsl = Color.parse(hex).hsl;
//       if (hsl[2] > 66) return 2;
//       else if (hsl[2] < 50) return 1;
//       else return 6;
//     });
//     return {
//       ...layer,
//       colors,
//     };
//   });
// };

export function showFirstHiddenLayer() {
  return (layers) => {
    // first we copy
    layers = layers.map((d) => ({ ...d }));
    // then modify
    for (let i = 0; i < layers.length; i++) {
      if (!layers[i].visible) {
        layers[i].visible = true;
        break;
      }
    }
    return layers;
  };
}

export function hideLastVisibleLayer(exceptFirst = false) {
  return (layers) => {
    // first we copy
    layers = layers.map((d) => ({ ...d }));
    // then modify
    for (let i = layers.length - 1; i >= 0; i--) {
      if (layers[i].visible) {
        const skip = i === 0 && exceptFirst;
        if (!skip) {
          layers[i].visible = false;
          break;
        }
      }
    }
    return layers;
  };
}

export function setRandomLayerCount(layers) {
  const n = random.rangeFloor(1, 6);
  return layers.map((layer, i) => {
    return {
      ...layer,
      visible: random.boolean(),
    };
  });
}

export function setLayerCount(n = 1) {
  return (layers) => {
    return layers.map((layer, i) => {
      return {
        ...layer,
        visible: i < n,
      };
    });
  };
}

export function mapVisibleLayers(layers, fn) {
  return layers.map((layer, i, lst) => {
    if (layer.visible) {
      return fn(layer, i, lst);
    } else {
      return { ...layer };
    }
  });
}
